import React, { useRef, useState, useCallback, useEffect } from 'react';
import { IonButton, IonIcon } from '@ionic/react';
import { micOutline, stopCircleOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { LoadingSpinner } from '../common/LoadingSpinner';

interface RecordingComponentProps {
  onTranscriptionComplete: (text: string) => Promise<void>;
  isLoading: boolean;
}

interface PermissionState {
  granted: boolean;
  stream: MediaStream | null;
}

export const RecordingComponent: React.FC<RecordingComponentProps> = ({
  onTranscriptionComplete,
  isLoading
}) => {
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const [permissionState, setPermissionState] = useState<PermissionState>({ granted: false, stream: null });
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const lastVoiceActivityRef = useRef<number>(Date.now());
  const isAndroid = Capacitor.getPlatform() === 'android';

  const checkMicrophonePermission = useCallback(async () => {
    try {
      // First check if permission is already granted
      if ('permissions' in navigator) {
        const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        if (permissionStatus.state === 'denied') {
          throw new Error('PERMISSION_DENIED');
        }
      }

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: { ideal: 1 },
          sampleRate: { ideal: 16000 },
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      setPermissionState({ granted: true, stream });
      return stream;
    } catch (error) {
      console.error('Permission error:', error);
      if (error instanceof Error) {
        if (error.message === 'PERMISSION_DENIED' || (error instanceof DOMException && error.name === 'NotAllowedError')) {
          setRecordingError(t('russianTutor.microphoneAccessDenied'));
        } else if (error instanceof DOMException && error.name === 'NotFoundError') {
          setRecordingError(t('russianTutor.microphoneNotFound'));
        } else {
          setRecordingError(t('russianTutor.recordingError'));
        }
      }
      setPermissionState({ granted: false, stream: null });
      throw error;
    }
  }, [t]);

  // Cleanup function for media streams
  const cleanupMediaStream = useCallback(() => {
    if (permissionState.stream) {
      permissionState.stream.getTracks().forEach(track => track.stop());
      setPermissionState(prev => ({ ...prev, stream: null }));
    }
  }, [permissionState.stream]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupMediaStream();
    };
  }, [cleanupMediaStream]);

  const stopRecording = useCallback(async () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      console.log('Stopping recording...');
      try {
        // Clear all timeouts
        if (recordingTimeoutRef.current) {
          clearTimeout(recordingTimeoutRef.current);
          recordingTimeoutRef.current = null;
        }
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
          silenceTimeoutRef.current = null;
        }

        // Stop recording and reset state
        mediaRecorderRef.current.stop();
        setIsRecording(false);
        
        // Clean up audio context and analyzer
        if (audioContextRef.current) {
          audioContextRef.current.close().catch(console.error);
          audioContextRef.current = null;
        }
        analyserRef.current = null;

        // Reset error state
        setRecordingError(null);

      } catch (error) {
        console.error('Error stopping recording:', error);
        setRecordingError(t('russianTutor.stopRecordingError'));
        setIsRecording(false);
      }
    }
  }, [t]);

  const checkForSilence = useCallback(() => {
    if (!analyserRef.current || isAndroid) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);

    // Calculate average frequency magnitude in human voice range (85-255Hz)
    const voiceRangeStart = Math.floor(85 * analyserRef.current.frequencyBinCount / (audioContextRef.current?.sampleRate || 16000));
    const voiceRangeEnd = Math.floor(255 * analyserRef.current.frequencyBinCount / (audioContextRef.current?.sampleRate || 16000));
    
    let sum = 0;
    for (let i = voiceRangeStart; i < voiceRangeEnd; i++) {
      sum += dataArray[i];
    }
    const average = sum / (voiceRangeEnd - voiceRangeStart);

    // If average is above threshold, consider it as voice activity
    if (average > 30) {
      lastVoiceActivityRef.current = Date.now();
    } else {
      const timeSinceLastVoice = Date.now() - lastVoiceActivityRef.current;
      if (timeSinceLastVoice > 5000) { // 5 seconds of silence
        stopRecording();
        return;
      }
    }

    // Continue checking every 100ms for better performance
    silenceTimeoutRef.current = setTimeout(checkForSilence, 100);
  }, [stopRecording, isAndroid]);

  const startRecording = async () => {
    try {
      setRecordingError(null);
      console.log('Requesting microphone access...');

      // Use existing stream or request new permissions
      const stream = permissionState.stream || await checkMicrophonePermission();

      // Set up audio analysis for web only
      if (!isAndroid) {
        audioContextRef.current = new AudioContext();
        const source = audioContextRef.current.createMediaStreamSource(stream);
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 2048;
        source.connect(analyserRef.current);
      }

      // Try different mime types if the default is not supported
      const mimeTypes = [
        isAndroid ? 'audio/mp4' : 'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4'
      ];

      let options;
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          options = {
            mimeType,
            audioBitsPerSecond: 128000
          };
          break;
        }
      }

      if (!options) {
        throw new Error('No supported mime type found for recording');
      }

      mediaRecorderRef.current = new MediaRecorder(stream, options);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          console.log('Received audio chunk:', event.data.size, 'bytes');
          audioChunksRef.current.push(event.data);
        }
      };

      // Start checking for silence on web only
      if (!isAndroid) {
        lastVoiceActivityRef.current = Date.now();
        checkForSilence();
      }

      mediaRecorderRef.current.onstop = async () => {
        console.log('Recording stopped, processing audio...');
        
        // Clean up audio analysis resources
        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
        }
        analyserRef.current = null;

        if (audioChunksRef.current.length === 0) {
          setRecordingError(t('russianTutor.noAudioData'));
          return;
        }

        const audioBlob = new Blob(audioChunksRef.current, { 
          type: isAndroid ? 'audio/mp4' : 'audio/webm' 
        });
        console.log('Audio blob size:', audioBlob.size, 'bytes');

        if (audioBlob.size < 100) {
          setRecordingError(t('russianTutor.audioTooShort'));
          return;
        }

        await sendAudioToSpeechToText(audioBlob);
        // Clean up the media stream
        stream.getTracks().forEach(track => track.stop());
      };

      // Start recording with 1-second timeslices for continuous data
      mediaRecorderRef.current.start(1000);
      setIsRecording(true);
      console.log('Recording started');

      // Auto-stop after 30 seconds
      recordingTimeoutRef.current = setTimeout(() => {
        if (isRecording) {
          stopRecording();
        }
      }, 30000);

    } catch (error) {
      console.error('Error starting recording:', error);
      setRecordingError(
        error instanceof DOMException && error.name === 'NotAllowedError'
          ? t('russianTutor.microphoneAccessDenied')
          : t('russianTutor.recordingError')
      );
      setIsRecording(false);
    }
  };

  const sendAudioToSpeechToText = async (audioBlob: Blob) => {
    try {
      console.log('Sending audio to Whisper API...');
      const formData = new FormData();
      formData.append('file', audioBlob, isAndroid ? 'audio.mp4' : 'audio.webm');
      formData.append('model', 'whisper-1');

      const response = await fetch('https://api.openai.com/v1/audio/transcriptions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Whisper API response:', data);

      if (data.text && data.text.trim()) {
        console.log('Transcribed text:', data.text);
        await onTranscriptionComplete(data.text);
      } else {
        throw new Error('No transcription received');
      }
    } catch (error) {
      console.error('Error in speech-to-text:', error);
      setRecordingError(t('russianTutor.transcriptionError'));
    }
  };

  const RecordingOverlay = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative flex flex-col items-center bg-[#1C1C1E] rounded-2xl p-8">
        <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center animate-pulse">
          <div className="w-12 h-12 rounded-full bg-red-500/30 flex items-center justify-center">
            <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
              <IonIcon icon={micOutline} className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>
        <div className="mt-6 text-center">
          <p className="text-white text-lg font-medium">Recording...</p>
          <p className="mt-2 text-white/70 text-sm max-w-xs text-center">
            {t('russianTutor.recordingHint')}
          </p>
          <IonButton
            onClick={stopRecording}
            fill="clear"
            className="mt-4"
          >
            <div className="flex items-center gap-2 text-red-500">
              <IonIcon icon={stopCircleOutline} className="w-5 h-5" />
              <span>Stop Recording</span>
            </div>
          </IonButton>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {isRecording && <RecordingOverlay />}
      <div className="flex gap-2 relative">
        {isLoading ? (
          <div className="h-12 w-12 flex items-center justify-center">
            <LoadingSpinner size="medium" color="#31D526" />
          </div>
        ) : isRecording ? (
          <IonButton
            onClick={stopRecording}
            className="h-12 w-12 rounded-full bg-red-500 transition-colors duration-300 shadow-lg m-0"
            disabled={isLoading}
          >
            <IonIcon icon={stopCircleOutline} className="w-6 h-6" />
          </IonButton>
        ) : (
          <IonButton
            onClick={startRecording}
            className="h-12 w-12 rounded-full bg-[#31D526] transition-colors duration-300 shadow-lg m-0"
            disabled={isLoading}
          >
            <IonIcon icon={micOutline} className="w-6 h-6" />
          </IonButton>
        )}
        {recordingError && (
          <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-red-500 text-white text-sm rounded-lg px-3 py-2 whitespace-nowrap z-10 min-w-max shadow-lg">
            {recordingError}
            <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-red-500 rotate-45" />
          </div>
        )}
      </div>
    </>
  );
};